import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/shared/components/PageHeader";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { Card } from "@/shared/components/ui/card";
import { KpiStrip, KpiTile } from "@/shared/components/ui/kpi";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useOutbox } from "@/features/global/use-global";
import { useTaskSummary } from "@/features/tasks/use-tasks";
import { useDashboard } from "./use-dashboard";
import type { DashboardWindow } from "./types";
import { TrendChart } from "./TrendChart";

const WINDOWS: { value: DashboardWindow; label: string }[] = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "all", label: "All" },
];
const pct = (n: number) => `${Math.round(n * 100)}%`;
const FUNNEL_MAX_PX = 320;
const TOP_GRID = "grid-cols-[2fr_1fr_1fr_1fr]";

function WindowToggle({ value, onChange }: { value: DashboardWindow; onChange: (w: DashboardWindow) => void }) {
  return (
    <div className="inline-flex rounded-ge-control border border-ge-border-card p-[2px]" role="group" aria-label="Time window">
      {WINDOWS.map((w) => (
        <button
          key={w.value}
          type="button"
          data-testid={`window-${w.value}`}
          aria-pressed={value === w.value}
          onClick={() => onChange(w.value)}
          className={cn(
            "rounded-ge-control px-[10px] py-[4px] font-mono text-[11px] uppercase tracking-ge-table transition-colors",
            value === w.value ? "bg-ge-active font-semibold text-ge-primary" : "text-ge-secondary hover:text-ge-primary",
          )}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Dashboard (ADR-0021) — the post-login landing. A tenant-wide "state of my campaigns":
 * headline KPIs + status, the acquisition→engagement funnel, deliverability rates, a trend
 * chart, top campaigns, and recent activity. Pipeline/status are all-time; delivery/trend/top
 * are within the selected window. Per-campaign drill lives on each campaign's Analytics tab.
 */
export function DashboardPage() {
  const [window, setWindow] = useState<DashboardWindow>("30d");
  const { data, isLoading, isError, refetch } = useDashboard(window);
  const outbox = useOutbox();
  // ADR-0027: the current user's manual-task load (tenant-wide for admins, own for a rep).
  const taskSummary = useTaskSummary();
  const tasksPending = taskSummary.data?.mine.pending ?? 0;
  const tasksOverdue = taskSummary.data?.mine.overdue ?? 0;

  if (isError) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <EmptyState
          message="Couldn't load your dashboard."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (!isLoading && data && data.campaigns.total === 0) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <EmptyState
          message="No campaigns yet — your analytics appear once you run outreach."
          action={
            <Link to="/campaigns/new">
              <Button>Create a campaign</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const d = data?.delivery;
  const p = data?.pipeline;
  const launched = data?.campaigns.byStatus?.LAUNCHED ?? 0;
  const openRate = d && d.sent > 0 ? pct(d.rates.open) : "--";

  const funnel = [
    { label: "Discovered", value: p?.discovered ?? 0 },
    { label: "Approved", value: p?.approved ?? 0 },
    { label: "Contacts", value: p?.contacts ?? 0 },
    { label: "Sent", value: d?.sent ?? 0 },
    { label: "Opened", value: d?.opened ?? 0, accent: true },
    { label: "Replied", value: d?.replied ?? 0, accent: true },
  ];
  const funnelMax = Math.max(1, ...funnel.map((r) => r.value));

  const rates = [
    { label: "Open rate", value: d?.rates.open ?? 0, tone: "accent" as const },
    { label: "Reply rate", value: d?.rates.reply ?? 0, tone: "value" as const },
    { label: "Bounce rate", value: d?.rates.bounce ?? 0, tone: "warning" as const },
    { label: "Unsub rate", value: d?.rates.unsubscribe ?? 0, tone: "warning" as const },
  ];

  const recent = (outbox.data ?? []).slice(0, 8);
  const val = (n?: number) => (data ? String(n ?? 0) : "--");

  return (
    <div>
      <PageHeader
        title="Dashboard"
        footnote="Your campaigns at a glance. Pipeline and status are all-time; delivery, rates and the trend are within the selected window."
        actions={<WindowToggle value={window} onChange={setWindow} />}
      />

      <KpiStrip>
        <KpiTile label="Campaigns" value={val(data?.campaigns.total)} />
        <KpiTile label="Launched" value={val(launched)} />
        <KpiTile label="Contacts" value={val(p?.contacts)} />
        <KpiTile label={`Sent (${window})`} value={val(d?.sent)} />
        <KpiTile label={`Open rate (${window})`} value={openRate} highlighted />
        <KpiTile label={tasksOverdue > 0 ? `My tasks (${tasksOverdue} overdue)` : "My tasks"} value={String(tasksPending)} highlighted={tasksOverdue > 0} />
      </KpiStrip>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        {/* Funnel */}
        <Card className="overflow-x-auto p-[18px]">
          <p className="ge-eyebrow flex items-center gap-1">FUNNEL <InfoHint text="How leads move through the pipeline across all your campaigns: discovered → approved → contacted → engaged." /></p>
          <div className="mt-4 flex min-w-[380px] flex-col gap-[10px]">
            {funnel.map((r) => (
              <div key={r.label} className="flex items-center gap-[10px]">
                <div className="w-[92px] shrink-0 text-right font-mono text-[11px] text-ge-secondary">{r.label}</div>
                <div
                  className="h-4 rounded-r-[4px]"
                  style={{
                    width: `${(r.value / funnelMax) * FUNNEL_MAX_PX}px`,
                    background: r.accent ? "var(--ge-accent)" : "var(--ge-chart-1)",
                    minWidth: r.value > 0 ? "2px" : "0",
                  }}
                />
                <div className={cn("font-mono text-[12px]", r.accent ? "text-ge-accent" : "text-ge-primary")}>{r.value}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[12px] text-ge-muted">
            Discovery → opens. Pipeline stages are all-time; Sent/Opened/Replied are within the {window} window and count
            unique recipients.
          </p>
        </Card>

        {/* Deliverability rates */}
        <Card className="p-[18px]">
          <p className="ge-eyebrow flex items-center gap-1">DELIVERABILITY ({window}) <InfoHint text="Send health over the selected window: delivered, bounced and unsubscribed rates. High bounces hurt your sender reputation." /></p>
          <div className="mt-4 flex flex-col gap-[14px]">
            {rates.map((r) => (
              <div key={r.label} className="flex items-center gap-[10px]" data-testid={`rate-${r.label}`}>
                <div className="w-[92px] shrink-0 font-mono text-[11px] text-ge-secondary">{r.label}</div>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-ge-active">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, Math.round(r.value * 100))}%`,
                      background: r.tone === "accent" ? "var(--ge-accent)" : r.tone === "warning" ? "var(--ge-warning)" : "var(--ge-chart-1)",
                    }}
                  />
                </div>
                <StatusText tone={r.tone} className="w-[44px] text-right">{data ? pct(r.value) : "--"}</StatusText>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[12px] text-ge-muted">
            Rates are over recipients sent within the window (unique), so they never exceed 100%.
          </p>
        </Card>
      </div>

      {/* Trend */}
      <Card className="mt-5 p-[18px]">
        <div className="flex items-center">
          <p className="ge-eyebrow flex items-center gap-1">TREND — SENT vs OPENED <InfoHint text="Daily sent vs opened over the window — the gap is a rough read on subject-line and timing performance." /></p>
          <div className="ml-auto flex items-center gap-[14px]">
            <div className="flex items-center gap-[6px]">
              <span className="h-2 w-2 rounded-[2px]" style={{ background: "var(--ge-chart-1)" }} />
              <span className="text-[11.5px] text-ge-secondary">Sent</span>
            </div>
            <div className="flex items-center gap-[6px]">
              <span className="h-2 w-2 rounded-[2px]" style={{ background: "var(--ge-accent)" }} />
              <span className="text-[11.5px] text-ge-secondary">Opened</span>
            </div>
          </div>
        </div>
        <div className="mt-4">{data ? <TrendChart data={data.trend} /> : <div className="h-[220px]" />}</div>
      </Card>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {/* Top campaigns */}
        <div>
          <p className="ge-eyebrow mb-2 flex items-center gap-1">TOP CAMPAIGNS ({window}) <InfoHint text="Your best-performing campaigns in the window, ranked by engagement. Click one to open it." /></p>
          <TableCard>
            <TableHeadRow grid={TOP_GRID}>
              <Th>Campaign</Th>
              <Th>Sent</Th>
              <Th>Opened</Th>
              <Th>Open rate</Th>
            </TableHeadRow>
            {(data?.topCampaigns ?? []).length === 0 ? (
              <div className="px-4 py-5 text-[12.5px] text-ge-secondary" data-testid="top-empty">
                No delivery activity in this window yet.
              </div>
            ) : (
              (data?.topCampaigns ?? []).map((c) => (
                <TableRow key={c.id} grid={TOP_GRID} data-testid={`top-row-${c.id}`}>
                  <Link to={`/campaigns/${c.id}`} className="truncate font-sans text-[13.5px] font-semibold text-ge-primary hover:text-ge-accent">
                    {c.name}
                  </Link>
                  <div className="font-mono text-[12px] text-ge-value">{c.sent || <Empty />}</div>
                  <div className="font-mono text-[12px] text-ge-value">{c.opened || <Empty />}</div>
                  <div className="font-mono text-[12px] text-ge-accent">{c.sent > 0 ? pct(c.openRate) : <Empty />}</div>
                </TableRow>
              ))
            )}
          </TableCard>
        </div>

        {/* Recent activity — reuses the Outbox feed */}
        <div>
          <p className="ge-eyebrow mb-2 flex items-center gap-1">RECENT ACTIVITY <InfoHint text="The latest delivery events across your campaigns (sent, opened, clicked, replied). The full log lives in Outbox." /></p>
          <Card className="p-[6px]">
            {recent.length === 0 ? (
              <div className="px-3 py-4 text-[12.5px] text-ge-secondary" data-testid="activity-empty">
                No delivery events yet.
              </div>
            ) : (
              <ul className="divide-y divide-ge-border-subtle">
                {recent.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 px-3 py-[9px]" data-testid={`activity-${e.id}`}>
                    <StatusText tone={e.type === "REPLY" ? "accent" : e.type === "BOUNCE" || e.type === "UNSUBSCRIBE" ? "warning" : "value"} className="w-[86px] shrink-0">
                      {e.type}
                    </StatusText>
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-ge-secondary">
                      {e.leadName ?? e.contactEmail ?? "—"}
                      {e.campaignName ? <span className="text-ge-muted"> · {e.campaignName}</span> : null}
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-ge-muted">
                      {e.at ? new Date(e.at).toLocaleDateString() : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
